'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { X, Cookie, Shield, Settings } from 'lucide-react'

interface CookiePreferences {
  necessary: boolean
  analytics: boolean
  marketing: boolean
}

const COOKIE_CONSENT_KEY = 'cookie-consent'
const COOKIE_PREFERENCES_KEY = 'cookie-preferences'

export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  })

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY)
    if (!consent) {
      // Small delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1000)
      return () => clearTimeout(timer)
    } else {
      const savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY)
      if (savedPreferences) {
        try {
          setPreferences(JSON.parse(savedPreferences))
        } catch {
          // Invalid JSON, reset to default
          localStorage.removeItem(COOKIE_PREFERENCES_KEY)
        }
      }
    }
  }, [])

  const saveConsent = (prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true')
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs))
    setPreferences(prefs)
    setIsVisible(false)

    // Here you would initialize analytics/marketing scripts based on preferences
    if (prefs.analytics) {
      // Initialize analytics (e.g., Google Analytics)
    }
    if (prefs.marketing) {
      // Initialize marketing scripts
    }
  }

  const acceptAll = () => {
    saveConsent({
      necessary: true,
      analytics: true,
      marketing: true,
    })
  }

  const acceptNecessary = () => {
    saveConsent({
      necessary: true,
      analytics: false,
      marketing: false,
    })
  }

  const saveCustomPreferences = () => {
    saveConsent(preferences)
  }

  if (!isVisible) return null

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6">
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
        {!showSettings ? (
          // Main banner
          <div className="p-4 md:p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 p-3 bg-primary-100 rounded-lg">
                <Cookie className="w-6 h-6 text-primary-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Používáme cookies
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Tato webová stránka používá cookies pro zajištění správného fungování,
                  analýzu návštěvnosti a zlepšování našich služeb. Kliknutím na &bdquo;Přijmout vše&ldquo;
                  souhlasíte s jejich používáním. Více informací najdete v našich{' '}
                  <Link href="/privacy" className="text-primary-600 hover:underline">
                    Zásadách ochrany osobních údajů
                  </Link>
                  .
                </p>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={acceptAll}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
                  >
                    Přijmout vše
                  </button>
                  <button
                    onClick={acceptNecessary}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    Pouze nezbytné
                  </button>
                  <button
                    onClick={() => setShowSettings(true)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors font-medium flex items-center gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    Nastavení
                  </button>
                </div>
              </div>
              <button
                onClick={acceptNecessary}
                className="flex-shrink-0 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Zavřít"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
          </div>
        ) : (
          // Settings panel
          <div className="p-4 md:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary-600" />
                Nastavení cookies
              </h3>
              <button
                onClick={() => setShowSettings(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              {/* Necessary cookies */}
              <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex-shrink-0 mt-0.5">
                  <input
                    type="checkbox"
                    checked={true}
                    disabled
                    className="w-5 h-5 text-primary-600 border-gray-300 rounded cursor-not-allowed opacity-60"
                  />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Nezbytné cookies</h4>
                  <p className="text-sm text-gray-500 mt-1">
                    Tyto cookies jsou nutné pro fungování webu a nelze je vypnout.
                    Zahrnují přihlášení, zabezpečení a základní funkce.
                  </p>
                </div>
                <span className="text-xs text-gray-400 font-medium">Vždy aktivní</span>
              </div>

              {/* Analytics cookies */}
              <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex-shrink-0 mt-0.5">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                    className="w-5 h-5 text-primary-600 border-gray-300 rounded cursor-pointer focus:ring-primary-500"
                  />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Analytické cookies</h4>
                  <p className="text-sm text-gray-500 mt-1">
                    Pomáhají nám pochopit, jak návštěvníci používají web.
                    Data jsou anonymizovaná a slouží ke zlepšování služby.
                  </p>
                </div>
              </div>

              {/* Marketing cookies */}
              <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex-shrink-0 mt-0.5">
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                    className="w-5 h-5 text-primary-600 border-gray-300 rounded cursor-pointer focus:ring-primary-500"
                  />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Marketingové cookies</h4>
                  <p className="text-sm text-gray-500 mt-1">
                    Používají se pro personalizaci obsahu a reklamy.
                    Můžeme je sdílet s reklamními partnery.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={saveCustomPreferences}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
              >
                Uložit nastavení
              </button>
              <button
                onClick={acceptAll}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Přijmout vše
              </button>
              <Link
                href="/privacy"
                className="px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors font-medium"
              >
                Více informací
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Hook for managing cookie preferences in components
export function useCookieConsent() {
  const [preferences, setPreferences] = useState<CookiePreferences | null>(null)
  const [hasConsent, setHasConsent] = useState(false)

  useEffect(() => {
    setHasConsent(!!localStorage.getItem(COOKIE_CONSENT_KEY))
    const saved = localStorage.getItem(COOKIE_PREFERENCES_KEY)
    if (saved) {
      try {
        setPreferences(JSON.parse(saved))
      } catch {
        // Invalid JSON, ignore
      }
    }
  }, [])

  return {
    preferences,
    hasConsent,
    canUseAnalytics: preferences?.analytics ?? false,
    canUseMarketing: preferences?.marketing ?? false,
  }
}
